/**
 * County lookup by ZIP, copied from production's `getCountiesByZipcode` and
 * its `County` model. No imports.
 */

export const DEFAULT_CHANNEL_LOOKUP_PATH = "/xapi/beamlocalchannellookupms/v1/channels/";

/** One county a ZIP resolves to. `dtvDMA` and `nielsenDMA` are strings, "undefined" included. */
export interface ZipCounty {
  /** Always "" — production reads `_id` where the lookup passes `id`. Unused; the key is fips + DMA. */
  id: string;
  zipcode: string;
  dtvDMA: string;
  nielsenDMA: string;
  fipsCountyCode: string;
  countyName: string;
  state: string;
  selected: boolean;
}

/** "SAINT JOHNS" -> "Saint Johns". An empty word stays empty. */
function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(" ")
    .map(word => (word ? word[0].toUpperCase() + word.substring(1) : word))
    .join(" ");
}

/**
 * Builds a county from one response row. Throws when `countyName` is missing,
 * as production does; the caller turns that into a failed lookup. A missing
 * DMA becomes the literal string "undefined" — production's `${value}` coercion.
 */
export function toZipCounty(row: any): ZipCounty {
  return {
    id: "",
    zipcode: row?.zipcode || "",
    dtvDMA: `${row?.dtvDMA}`,
    nielsenDMA: `${row?.nielsenDMA}`,
    fipsCountyCode: `${row?.fipsCountyCode}`,
    countyName: titleCase(row.countyName),
    state: row?.state || "",
    selected: false
  };
}

/** The option key and the id the dropdown selects by: fips + DTV DMA. */
export function countyKey(county: ZipCounty): string {
  return `${county.fipsCountyCode}${county.dtvDMA}`;
}

/** "Fulton, GA". */
export function countyLabel(county: ZipCounty): string {
  return `${titleCase(county.countyName)}, ${county.state}`;
}

export interface GetCountiesOptions {
  /** Base path of the channel lookup service, trailing slash included. Defaults to production's. */
  channelLookupPath?: string;
  /** Cancels the request; an aborted fetch resolves to `undefined` like any other failure. */
  signal?: AbortSignal;
}

/**
 * Resolves to the counties for a ZIP, `[]` for none, or `undefined` for ANY
 * failure — never rejects, as in production.
 *
 * ⚠ An HTTP 4xx/5xx is not itself a failure there: the body is still parsed,
 * so a 500 only reads as an error because its body is not an array. A 500
 * carrying an array would read as results. Kept.
 */
export async function getCountiesByZip(
  zip: string,
  { channelLookupPath = DEFAULT_CHANNEL_LOOKUP_PATH, signal }: GetCountiesOptions = {}
): Promise<ZipCounty[] | undefined> {
  try {
    const response = await fetch(`${channelLookupPath}county/zipcode/${zip}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal
    });
    const data = await response?.json();

    if (data?.length === 0) {
      return [];
    }
    if (!Array.isArray(data)) {
      return undefined;
    }
    return data.map(toZipCounty);
  } catch {
    return undefined;
  }
}
